import { Component, ElementRef, EventEmitter, HostListener, Output } from '@angular/core';
import { AuthService } from '@services/auth/auth.service';
import { ViewportService } from '@services/core/viewport.service';
import { LayoutService } from '@services/layout/layout.service';
import { ThemeService } from '@services/layout/theme.service';
import { MenuService } from '@services/shared/menu.service';
import { SharedModule } from '@shared/modules/shared.module';
import { MenuItem } from 'primeng/api';
import { ItemMenuComponent } from '../item-menu/item-menu.component';

@Component({
	selector: 'app-sidebar',
	imports: [SharedModule, ItemMenuComponent],
	templateUrl: './sidebar.component.html',
	styleUrl: './sidebar.component.scss',
})
export class AppSidebarComponent {
	// Arrays defining the menu structure for the sidebar
	modelUpper: any[] = [];
	modelLower: any[] = [];
	modelResponsive: any[] = [];

	isSidebarOpen = false;
	wasMobile = false;
	currentTheme: 'light' | 'dark' = 'light';

	@Output() sidebarToggle = new EventEmitter<boolean>();

	toggleSidebar() {
		this.isSidebarOpen = !this.isSidebarOpen;
		this.sidebarToggle.emit(this.isSidebarOpen);
	}

	constructor(
		public layoutService: LayoutService,
		public el: ElementRef,
		public authService: AuthService,
		private themeService: ThemeService,
		private viewportService: ViewportService,
		private menuService: MenuService,
	) {}

	screenWidth: number = window.innerWidth;

	/**
	 * Listener for window resize events. Automatically opens the sidebar when switching
	 * from desktop to mobile view.
	 */
	@HostListener('window:resize', [])
	onResize(): void {
		this.screenWidth = window.innerWidth;

		if (this.screenWidth >= 1500) {
			this.isSidebarOpen = true; // Se abre si la pantalla es mayor o igual a 1400px
		} else if (this.screenWidth <= 844) {
			this.isSidebarOpen = true; // Se mantiene abierto en móvil
		} else {
			this.isSidebarOpen = false; // Se cierra solo entre 844px y 1400px
		}
		const isMobile = this.viewportService.isMobileView();

		if (isMobile && !this.wasMobile) {
			this.menuService.openMenu('sidebar'); // Ensure sidebar is open in mobile view
		}

		this.wasMobile = isMobile; // Update the previous state
	}

	/**
	 * Determines whether the current viewport is mobile or not.
	 * @returns true if the viewport is mobile, false otherwise
	 */
	isMobile(): boolean {
		return this.viewportService.isMobileView();
	}

	/**
	 * Handles menu item click events. Closes the backoffice in mobile view
	 * when a menu item is clicked, except for items with submenus.
	 */
	handleMenuItemClick(item: MenuItem): void {
		// Si estamos en móvil y el elemento NO tiene submenús, cerramos el menú
		if (this.isMobile() && (!item.items || item.items.length === 0)) {
			this.menuService.closeMenu('sidebar');
			this.isSidebarOpen = true;
			this.sidebarToggle.emit(false);
		}

		// Si el elemento tiene items (submenús), no hacemos nada adicional
		// ya que el componente ItemMenuComponent ya maneja la expansión/colapso
		// a través de su método toggleSubmenu()
	}

	handleLogout(): void {
		this.authService.logout().subscribe({
			next: () => {},
			error: (err) => console.error('❌ Error en logout:', err),
		});
	}

	userRole: string | null = null;
	ngOnInit() {
		this.userRole = localStorage.getItem('USER_ROLE');

		// Forzar sidebar cerrado al iniciar
		this.menuService.closeMenu('sidebar');

		// Sync the sidebar state with the service
		this.menuService.menuState$.subscribe((state) => {
			this.isSidebarOpen = state['sidebar'] ?? false;
			this.sidebarToggle.emit(this.isSidebarOpen);
		});

		// Define the upper section of the sidebar menu
		this.modelUpper = [
			{
				logo: 'assets/layout/images/logo-complete.png',
			},
			{
				label: 'Dashboard',
				id: 'dashboard',
				icon: 'LayoutDashboard',
				routerLink: ['dashboard'],
			},
			{
				label: 'Consultas',
				id: 'consultations',
				icon: 'MessageSquareText',
				routerLink: ['consultations'],
			},
		];

		// 🔸 Agregar separador si hay sección extra
		this.modelUpper.push({ separator: true });

		// 🔹 Submenú común para todos
		const configItem: any = {
			label: 'Configuración',
			id: 'configuration',
			icon: 'Settings',
			items: [
				{
					label: 'Contraseña',
					id: 'update-password',
					icon: 'Lock',
					routerLink: ['settings', 'update-password'],
				},
			],
		};

		// 🔹 Submenú para admin y super_admin
		if (this.userRole === 'admin' || this.userRole === 'super_admin') {
			configItem.items.push(
				{
					label: 'Abogados',
					id: 'users',
					icon: 'User',
					routerLink: ['users'],
				},
				{
					label: 'Categorías',
					id: 'categories',
					icon: 'Tag',
					routerLink: ['categories'],
				},
				{
					label: 'Roles',
					id: 'roles',
					icon: 'User',
					routerLink: ['roles'],
				},
				{
					label: 'Clientes',
					id: 'clients',
					icon: 'User',
					routerLink: ['clients'],
				},
				{
					label: 'Respuestas',
					id: 'quick-response',
					icon: 'MessageSquareText',
					routerLink: ['quick-response'],
				},
				{
					label: 'General',
					id: 'general-configuration',
					icon: 'Settings2',
					routerLink: ['settings', 'general-configuration'],
				},
				{
					label: 'Emails',
					id: 'emails',
					icon: 'MessageSquareText',
					routerLink: ['settings', 'emails'],
				},
			);
		}

		// 🔹 Push final
		this.modelUpper.push(configItem);

		// Define the lower section of the sidebar menu
		this.modelLower = [
			{
				type: 'button',
				id: 'isSidebarOpen',
				label: 'Cerrar panel',
				icon: 'PanelLeftClose',
				action: () => this.toggleSidebar(),
			},
			{
				type: 'button',
				id: 'lightMode',
				label: 'Claro',
				icon: 'Sun',
				action: () => this.switchTheme('light'),
			},
			{
				type: 'button',
				id: 'darkMode',
				label: 'Oscuro',
				icon: 'Moon',
				action: () => this.switchTheme('dark'),
			},
		];

		// Define responsive menu for mobile view
		this.modelResponsive = [
			{
				label: 'Logout',
				id: 'logout',
				icon: 'logOut',
				action: () => this.handleLogout(),
			},
			{
				type: 'button',
				id: 'lightMode',
				label: 'Claro',
				icon: 'Sun',
				action: () => this.switchTheme('light'),
			},
			{
				type: 'button',
				id: 'darkMode',
				label: 'Oscuro',
				icon: 'Moon',
				action: () => this.switchTheme('dark'),
			},
		];

		// Subscribe to theme changes
		this.themeService.theme$.subscribe((theme) => {
			this.currentTheme = theme as 'light' | 'dark';
		});

		this.themeService.setTheme(this.currentTheme); // Apply the current theme
	}

	/**
	 * Cambia el tema entre modo claro y oscuro
	 * @param theme Opcional: tema específico a aplicar
	 */
	switchTheme(theme?: 'light' | 'dark') {
		const newTheme = theme || (this.currentTheme === 'light' ? 'dark' : 'light');
		this.themeService.setTheme(newTheme);
	}

	isLightTheme(): boolean {
		return this.currentTheme === 'light';
	}

	isDarkTheme(): boolean {
		return this.currentTheme === 'dark';
	}
}
